// Placeholder source descriptors — pure code, no PNG assets.
//
// When an entity has no uploaded image (avatar/cover/market/outcome), the
// manifest carries a synthetic source that the renderer evaluates inline
// via the brightness functions in `core/brightness-fns.ts`. No fetch, no
// IPFS round-trip, no committed PNG.
//
// Per-pubkey / per-userId / per-outcomeIndex jitter is applied via the
// manifest's outer `seed` field (XORed with the placeholder baseSeed for
// markets) — not here.

import type { GenerativeVariant } from '../types';

export interface PlaceholderSource {
  readonly variant: GenerativeVariant;
  /** Base seed XORed with the entity's own seed for per-instance jitter. */
  readonly baseSeed: number;
}

/**
 * Per-category placeholder source. Variant carries category identity at
 * the brightness-shape level (e.g. crypto = ring lattice, gaming = noise
 * burst). Tweak constants to retune; every market in that category re-skins
 * automatically.
 *
 * Keys MUST match `id` values in `PROPOSAL_CATEGORIES` from
 * `frontend/src/config/proposal-options.ts`.
 */
export const CATEGORY_PLACEHOLDER_SOURCE: Readonly<Record<string, PlaceholderSource>> = {
  politics: { variant: 'rings', baseSeed: 0x504f },
  sports: { variant: 'waves', baseSeed: 0x5350 },
  crypto: { variant: 'rings', baseSeed: 0x4347 },
  finance: { variant: 'stripes', baseSeed: 0x4649 },
  economics: { variant: 'stripes', baseSeed: 0x4543 },
  technology: { variant: 'noise', baseSeed: 0x5445 },
  entertainment: { variant: 'waves', baseSeed: 0x454e },
  culture: { variant: 'bloom', baseSeed: 0x4355 },
  music: { variant: 'waves', baseSeed: 0x4d55 },
  memes: { variant: 'noise', baseSeed: 0x4d45 },
  gaming: { variant: 'noise', baseSeed: 0x4741 },
  health: { variant: 'waves', baseSeed: 0x4845 },
  science: { variant: 'rings', baseSeed: 0x5343 },
  environment: { variant: 'bloom', baseSeed: 0x454f },
  food: { variant: 'rings', baseSeed: 0x464f },
  world: { variant: 'waves', baseSeed: 0x574f },
  law: { variant: 'stripes', baseSeed: 0x4c41 },
  other: { variant: 'noise', baseSeed: 0x4f54 },
};

/** Fallback used when a market's category isn't in the map above. */
export const DEFAULT_CATEGORY_PLACEHOLDER: PlaceholderSource = {
  variant: 'noise',
  baseSeed: 0x4f54,
};

export const AVATAR_PLACEHOLDER_VARIANT: GenerativeVariant = 'noise';
export const COVER_PLACEHOLDER_VARIANT: GenerativeVariant = 'breakthrough';
export const OUTCOME_PLACEHOLDER_VARIANT: GenerativeVariant = 'rings';

/**
 * Resolve a category id to its placeholder source descriptor. Falls back
 * to `DEFAULT_CATEGORY_PLACEHOLDER` for unknown / missing categories so
 * the renderer always has a valid source.
 */
export function resolveCategoryPlaceholderSource(
  category: string | null | undefined,
): PlaceholderSource {
  if (typeof category !== 'string' || category.length === 0) return DEFAULT_CATEGORY_PLACEHOLDER;
  return CATEGORY_PLACEHOLDER_SOURCE[category] ?? DEFAULT_CATEGORY_PLACEHOLDER;
}
